import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody, Badge, SectionLabel,
  PrimaryButton, SecondaryButton, FormGroup, Input, Select, Textarea,
  ModalOverlay, ModalDialog, ModalTitle, ModalBody, ModalActions,
} from '../styled-components';
import { SkeletonRows, EmptyState, StyledCheckboxBox } from '../components';
import { useFetchData, showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';
import { Lock, FileText, Megaphone, ShieldCheck, Phone, ArrowLeft, Plus, Copy, Check, PhoneOff } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComplianceForm {
  id: string;
  form_type: string;
  status: string;
  campaign_id: string | null;
  phone_number_id: string | null;
  use_case_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  data?: Record<string, any>;
  status_history?: { status: string; at: string | null; by: string | null; notes: string | null }[];
}

interface CompliancePhoneNumber {
  phone_number_id: string;
  phone_number: string;
  label: string;
  assigned_use_case: string | null;
  messaging_use_case: string | null;
}

interface UseCase {
  id: string;
  name: string;
  use_case_type: string;
  phone_number_ids: string[];
  phone_numbers: { phone_number_id: string; phone_number: string }[];
  sms_campaign_form: { id: string; status: string } | null;
  analytics_provider_form: { id: string; status: string } | null;
}

interface DncEntry {
  phone_number: string;
  added_datetime: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GENERAL_FORMS = [
  { type: 'outbound_dialing', label: 'Outbound Dialing Permissions Request', description: 'Register your business for outbound voice calling' },
] as const;

const STATUS_BADGE_VARIANT: Record<string, string> = {
  draft: 'default',
  pending_review: 'invited',
  changes_requested: 'paused',
  approved: 'active',
  rejected: 'error',
  revoked: 'error',
  not_registered: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  changes_requested: 'Changes Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  revoked: 'Revoked',
  not_registered: 'Not Registered',
};

const FORM_TITLES: Record<string, string> = {
  outbound_dialing: 'Outbound Permissions Request',
  sms_brand: 'SMS Brand Registration',
  analytics_provider: 'Analytics Provider Attestation',
  cnam: 'CNAM Registration (Branded Caller ID)',
  sms_campaign: 'SMS Campaign Registration',
  sms_phone_number: 'SMS Phone Number Registration',
  port_number: 'Port a Number — Letter of Authorization',
};

const USE_CASE_TYPES = [
  'Abandoned Cart', 'Appointment Reminders', 'Appointment Scheduling', 'Asset Management',
  'Automated Support', 'Call Tracking', 'Click to Call', 'Contact Tracing',
  'Contactless Delivery', 'Customer Support', 'Dating/Social', 'Delivery Notifications',
  'Distance Learning', 'Emergency Notifications', 'Employee Notifications', 'Exam Proctoring',
  'Field Notifications', 'First Responder', 'Fraud Alerts', 'Group Messaging',
  'Identify & Verification', 'Intelligent Routing', 'Lead Alerts', 'Lead Distribution',
  'Lead Generation', 'Lead Management', 'Lead Nurturing', 'Marketing Events',
  'Mass Alerts', 'Meetings/Collaboration', 'Order Notifications', 'Outbound Dialer',
  'Pharmacy', 'Phone System', 'Purchase Confirmation', 'Remote Appointments',
  'Rewards Program', 'Self-Service', 'Service Alerts', 'Shift Management',
  'Survey/Research', 'Telehealth', 'Telemarketing', 'Therapy (Individual+Group)', 'Tutoring',
].map(v => ({ value: v, label: v }));

const COMPANY_TYPES = [
  { value: 'Public', label: 'Public' },
  { value: 'Private', label: 'Private' },
  { value: 'US Nonprofit', label: 'US Nonprofit' },
  { value: 'US Government', label: 'US Government' },
];

// ---------------------------------------------------------------------------
// Field validators (mirror backend patterns from compliance.py)
// ---------------------------------------------------------------------------

const EIN_RE = /^\d{2}-\d{7}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CNAM_RE = /^[A-Za-z][A-Za-z0-9., ]{0,14}$/;

function isValidEIN(v: string) { return !v || EIN_RE.test(v); }
function isValidEmail(v: string) { return !v || EMAIL_RE.test(v); }
function isValidPhone(v: string) { return v.replace(/\D/g, '').length >= 10; }
function isValidURL(v: string) { return !v || v.startsWith('https://'); }
function isValidZip(v: string) { return !v || /^\d{5}(-\d{4})?$/.test(v); }
function isValidCNAM(v: string) { return !v || CNAM_RE.test(v); }

function formatPhone(num: string): string {
  const digits = num.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return num;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Section = styled.div({ marginBottom: 'var(--section-gap)' });

const FormRow = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
});

const FormCard = styled.div<{ locked?: boolean }>(({ locked }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 20px',
  borderBottom: '1px solid var(--bg-border)',
  opacity: locked ? 0.5 : 1,
  cursor: locked ? 'not-allowed' : 'pointer',
  transition: 'background 120ms',
  ':hover': locked ? {} : { background: 'var(--bg-surface-hover)' },
  ':last-child': { borderBottom: 'none' },
}));

const FormInfo = styled.div({ display: 'flex', flexDirection: 'column' as const, gap: 4 });
const FormLabel = styled.span({ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' });
const FormDescription = styled.span({ fontSize: 12, color: 'var(--text-muted)' });

const BackButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: 13,
  padding: '4px 0',
  marginBottom: 8,
  ':hover': { color: 'var(--text-primary)' },
});

const StyledCheckboxWrapper = styled.div({
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
});

function Checkbox({ checked, onChange, disabled, hasError, children }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hasError?: boolean; children?: React.ReactNode;
}) {
  return (
    <StyledCheckboxWrapper onClick={disabled ? undefined : () => onChange(!checked)}>
      <StyledCheckboxBox checked={checked} hasError={hasError} disabled={disabled} />
      {children && <span>{children}</span>}
    </StyledCheckboxWrapper>
  );
}

const ReviewNotesBox = styled.div({
  padding: '12px 16px',
  background: 'rgba(255, 179, 0, 0.08)',
  border: '1px solid rgba(255, 179, 0, 0.25)',
  borderRadius: 'var(--radius-card)',
  fontSize: 13,
  color: 'var(--text-primary)',
  marginBottom: 16,
});

const UseCaseCard = styled.div({
  padding: '16px 20px',
  borderBottom: '1px solid var(--bg-border)',
  ':last-child': { borderBottom: 'none' },
});

const UseCaseHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
});

const UseCaseName = styled.span({ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' });

const UseCaseActions = styled.div({ display: 'flex', gap: 6, flexWrap: 'wrap' as const });

const ActionChip = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  borderRadius: 20,
  cursor: 'pointer',
  transition: 'all 120ms ease',
  border: '1px solid var(--bg-border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  ':hover': {
    borderColor: 'var(--accent-cyan)',
    color: 'var(--accent-cyan)',
    background: 'var(--accent-cyan-glow)',
  },
});

const RegisterChip = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  borderRadius: 20,
  cursor: 'pointer',
  transition: 'all 120ms ease',
  border: '1px solid var(--bg-border)',
  background: 'transparent',
  color: 'var(--text-muted)',
  ':hover': {
    borderColor: 'var(--accent-cyan)',
    color: 'var(--accent-cyan)',
    background: 'var(--accent-cyan-glow)',
  },
});

const UseCaseDetail = styled.div({
  fontSize: 12,
  color: 'var(--text-muted)',
  marginTop: 4,
});

const CopyButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'none',
  border: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  fontSize: 11,
  padding: '2px 4px',
  borderRadius: 4,
  ':hover': { color: 'var(--text-primary)', background: 'var(--bg-surface-hover)' },
});

const PhoneScrollArea = styled.div({
  maxHeight: 400,
  overflowY: 'auto' as const,
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--bg-border)',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'var(--text-muted)',
  },
  scrollbarWidth: 'thin' as 'auto' | 'thin' | 'none',
  scrollbarColor: 'var(--bg-border) transparent',
});

const SectionHeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
});


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function CompliancePage() {
  const [editingForm, setEditingForm] = useState<{
    formType: string;
    formId?: string;
    prefillFromId?: string;
    phoneNumberId?: string;
    useCaseId?: string;
    useCaseType?: string;
  } | null>(null);
  const location = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.key triggers the reset on navigation
  useEffect(() => {
    setEditingForm(null);
  }, [location.key]);

  const viewKey = editingForm ? `edit-${editingForm.formType}-${editingForm.formId ?? editingForm.prefillFromId ?? 'new'}` : 'list';

  return (
    <div key={viewKey} style={{ animation: 'pageIn 200ms ease both', flex: 1, display: 'flex', flexDirection: 'column' }}>
      {editingForm ? (
        <FormEditorView
          formType={editingForm.formType}
          formId={editingForm.formId}
          prefillFromId={editingForm.prefillFromId}
          phoneNumberId={editingForm.phoneNumberId}
          useCaseId={editingForm.useCaseId}
          useCaseType={editingForm.useCaseType}
          onBack={() => setEditingForm(null)}
        />
      ) : (
        <ComplianceListView onEditForm={setEditingForm} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List view (four sections)
// ---------------------------------------------------------------------------

interface ListViewProps {
  onEditForm: (params: { formType: string; formId?: string; prefillFromId?: string; phoneNumberId?: string; useCaseId?: string; useCaseType?: string }) => void;
}

function ComplianceListView({ onEditForm }: ListViewProps) {
  const { data: forms } = useFetchData<ComplianceForm[]>('/dashboard/api/compliance/forms', []);
  const { data: phoneNumbers, loading: phonesLoading } = useFetchData<CompliancePhoneNumber[]>('/dashboard/api/compliance/phone-numbers', []);
  const { data: useCases, loading: useCasesLoading, refetch: refetchUseCases } = useFetchData<UseCase[]>('/dashboard/api/compliance/use-cases', []);
  const [showCreateUseCase, setShowCreateUseCase] = useState(false);
  const [showAssignPhoneNumbers, setShowAssignPhoneNumbers] = useState<string | null>(null);

  const formsByType: Record<string, ComplianceForm> = {};
  for (const f of forms) {
    const existing = formsByType[f.form_type];
    if (existing && existing.status !== 'rejected' && existing.status !== 'revoked') continue;
    formsByType[f.form_type] = f;
  }

  // Gating: outbound submitted means status is pending_review, approved, or changes_requested
  const outboundForm = formsByType.outbound_dialing;
  const outboundSubmitted = outboundForm && outboundForm.status === 'approved';

  // Index forms by phone_number_id + form_type for per-number lookups
  const phoneFormIndex: Record<string, Record<string, ComplianceForm>> = {};
  for (const f of forms) {
    if (f.phone_number_id && f.form_type === 'cnam') {
      if (!phoneFormIndex[f.phone_number_id]) phoneFormIndex[f.phone_number_id] = {};
      if (!phoneFormIndex[f.phone_number_id][f.form_type] || (f.status !== 'rejected' && f.status !== 'revoked')) {
        phoneFormIndex[f.phone_number_id][f.form_type] = f;
      }
    }
  }

  // Port number form
  const portForm = forms.find(f => f.form_type === 'port_number' && f.status !== 'rejected' && f.status !== 'revoked');

  function handleGeneralFormClick(type: string) {
    const existing = formsByType[type];
    if (existing && existing.status !== 'rejected' && existing.status !== 'revoked') {
      onEditForm({ formType: type, formId: existing.id });
    } else {
      onEditForm({ formType: type, prefillFromId: existing?.id });
    }
  }

  function handlePhoneFormClick(phoneNumberId: string, formType: 'cnam') {
    const existing = phoneFormIndex[phoneNumberId]?.[formType];
    if (existing && existing.status !== 'rejected' && existing.status !== 'revoked') {
      onEditForm({ formType, formId: existing.id, phoneNumberId });
    } else {
      onEditForm({ formType, phoneNumberId, prefillFromId: existing?.id });
    }
  }

  function handleUseCaseFormClick(useCaseId: string, formType: 'sms_campaign' | 'analytics_provider', existingForm: { id: string; status: string } | null, useCaseType?: string) {
    if (existingForm && existingForm.status !== 'rejected' && existingForm.status !== 'revoked') {
      onEditForm({ formType, formId: existingForm.id, useCaseId, useCaseType });
    } else {
      onEditForm({ formType, useCaseId, useCaseType, prefillFromId: existingForm?.id });
    }
  }

  return (
    <div>
      <PageHeader><PageTitle>Compliance</PageTitle></PageHeader>

      {/* Section 1: General Forms */}
      <Section>
        <SectionLabel>General Forms</SectionLabel>
        <Card>
          {GENERAL_FORMS.map(({ type, label, description }) => {
            const form = formsByType[type];
            const locked = type !== 'outbound_dialing' && !outboundSubmitted;
            return (
              <FormCard key={type} locked={locked} onClick={() => !locked && handleGeneralFormClick(type)}>
                <FormInfo>
                  <FormLabel style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {locked && <Lock size={14} />}
                    {label}
                  </FormLabel>
                  <FormDescription>
                    {locked ? 'Requires Outbound Permissions to be submitted' : description}
                  </FormDescription>
                </FormInfo>
                {form ? (
                  <Badge variant={STATUS_BADGE_VARIANT[form.status] || 'default'}>
                    {STATUS_LABEL[form.status] || form.status}
                  </Badge>
                ) : (
                  <Badge variant="default">{locked ? 'Locked' : 'Not Started'}</Badge>
                )}
              </FormCard>
            );
          })}
        </Card>
      </Section>

      {/* Section 2: Use Cases */}
      <Section>
        <SectionHeaderRow>
          <SectionLabel>Use Cases</SectionLabel>
          {outboundSubmitted && (
            <ActionChip onClick={() => setShowCreateUseCase(true)}>
              <Plus size={12} /> New Use Case
            </ActionChip>
          )}
        </SectionHeaderRow>
        <Card>
          {!outboundSubmitted ? (
            <FormCard locked>
              <FormInfo>
                <FormLabel style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Lock size={14} /> Use Cases
                </FormLabel>
                <FormDescription>Requires Outbound Permissions to be submitted</FormDescription>
              </FormInfo>
              <Badge variant="default">Locked</Badge>
            </FormCard>
          ) : useCasesLoading ? (
            <div style={{ padding: 20 }}><SkeletonRows cols={3} rows={2} /></div>
          ) : useCases.length === 0 ? (
            <div style={{ padding: 20 }}>
              <EmptyState icon={<FileText size={32} />} title="No use cases" body="Create a use case to group campaigns and register for SMS." />
            </div>
          ) : useCases.map(uc => (
            <UseCaseCard key={uc.id}>
              <UseCaseHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <UseCaseName>{uc.name}</UseCaseName>
                  <Badge variant="default">{uc.use_case_type}</Badge>
                </div>
                <UseCaseActions>
                  <ActionChip onClick={() => handleUseCaseFormClick(uc.id, 'sms_campaign', uc.sms_campaign_form, uc.use_case_type)}>
                    <Megaphone size={12} />
                    SMS Campaign
                    {uc.sms_campaign_form && (
                      <Badge variant={STATUS_BADGE_VARIANT[uc.sms_campaign_form.status] || 'default'} style={{ fontSize: 9, padding: '1px 5px' }}>
                        {STATUS_LABEL[uc.sms_campaign_form.status]}
                      </Badge>
                    )}
                  </ActionChip>
                  <ActionChip onClick={() => handleUseCaseFormClick(uc.id, 'analytics_provider', uc.analytics_provider_form, uc.use_case_type)}>
                    <ShieldCheck size={12} />
                    Analytics
                    {uc.analytics_provider_form && (
                      <Badge variant={STATUS_BADGE_VARIANT[uc.analytics_provider_form.status] || 'default'} style={{ fontSize: 9, padding: '1px 5px' }}>
                        {STATUS_LABEL[uc.analytics_provider_form.status]}
                      </Badge>
                    )}
                  </ActionChip>
                  <ActionChip onClick={() => setShowAssignPhoneNumbers(uc.id)}>
                    <Plus size={12} /> Phone Number
                  </ActionChip>
                </UseCaseActions>
              </UseCaseHeader>
              {uc.phone_numbers.length > 0 ? (
                <UseCaseDetail style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {uc.phone_numbers.map(pn => (
                    <CopyNumber key={pn.phone_number_id} number={pn.phone_number} />
                  ))}
                </UseCaseDetail>
              ) : (
                <UseCaseDetail>
                  <span style={{ color: 'var(--text-muted)' }}>No phone numbers assigned</span>
                </UseCaseDetail>
              )}
            </UseCaseCard>
          ))}
        </Card>
      </Section>

      {/* Section 3: Phone Numbers */}
      <Section>
        <SectionLabel>Phone Numbers</SectionLabel>
        <Card>
          {!outboundSubmitted ? (
            <FormCard locked>
              <FormInfo>
                <FormLabel style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Lock size={14} /> Phone Numbers
                </FormLabel>
                <FormDescription>Requires Outbound Permissions to be submitted</FormDescription>
              </FormInfo>
              <Badge variant="default">Locked</Badge>
            </FormCard>
          ) : (
            <PhoneScrollArea>
            <Table>
              <Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' }}>
                <tr>
                  <th>Number</th>
                  <th style={{ textAlign: 'center' }}>CNAM (Branded Caller ID)</th>
                </tr>
              </Thead>
              <Tbody>
                {phonesLoading ? (
                  <SkeletonRows cols={2} rows={3} />
                ) : phoneNumbers.length === 0 ? (
                  <tr><td colSpan={2}><EmptyState icon={<Phone size={32} />} title="No phone numbers" body="Purchase a phone number to register for CNAM." /></td></tr>
                ) : phoneNumbers.map(n => {
                  const cnamForm = phoneFormIndex[n.phone_number_id]?.cnam;
                  return (
                    <tr key={n.phone_number_id}>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatPhone(n.phone_number)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <PhoneFormCell
                          form={cnamForm}
                          onRegister={() => handlePhoneFormClick(n.phone_number_id, 'cnam')}
                          onView={() => handlePhoneFormClick(n.phone_number_id, 'cnam')}
                          registerLabel="Register"
                        />
                      </td>
                    </tr>
                  );
                })}
              </Tbody>
            </Table>
            </PhoneScrollArea>
          )}
        </Card>
      </Section>

      {/* Section 4: Port a Number */}
      <Section>
        <SectionLabel>Port a Number</SectionLabel>
        <Card>
          {!outboundSubmitted ? (
            <FormCard locked>
              <FormInfo>
                <FormLabel style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Lock size={14} /> Port a Number
                </FormLabel>
                <FormDescription>Requires Outbound Permissions to be submitted</FormDescription>
              </FormInfo>
              <Badge variant="default">Locked</Badge>
            </FormCard>
          ) : (
            <FormCard onClick={() => {
              if (portForm) {
                onEditForm({ formType: 'port_number', formId: portForm.id });
              } else {
                const prevPort = forms.find(f => f.form_type === 'port_number' && (f.status === 'rejected' || f.status === 'revoked'));
                onEditForm({ formType: 'port_number', prefillFromId: prevPort?.id });
              }
            }}>
              <FormInfo>
                <FormLabel>Port an existing number to Guava</FormLabel>
                <FormDescription>Fill out a Letter of Authorization to port your phone number from another provider</FormDescription>
              </FormInfo>
              {portForm ? (
                <Badge variant={STATUS_BADGE_VARIANT[portForm.status] || 'default'}>
                  {STATUS_LABEL[portForm.status] || portForm.status}
                </Badge>
              ) : (
                <Badge variant="default">Start LOA</Badge>
              )}
            </FormCard>
          )}
        </Card>
      </Section>

      {/* Section 5: Do Not Call List */}
      <DncSection outboundSubmitted={outboundSubmitted} />

      {showCreateUseCase && (
        <CreateUseCaseModal
          onClose={() => setShowCreateUseCase(false)}
          onCreated={() => { setShowCreateUseCase(false); refetchUseCases(); }}
        />
      )}
      {showAssignPhoneNumbers && (
        <AssignPhoneNumbersModal
          useCaseId={showAssignPhoneNumbers}
          useCaseName={useCases.find(u => u.id === showAssignPhoneNumbers)?.name || ''}
          existingPhoneNumberIds={useCases.find(u => u.id === showAssignPhoneNumbers)?.phone_number_ids || []}
          phoneNumbers={phoneNumbers}
          onClose={() => setShowAssignPhoneNumbers(null)}
          onSaved={() => { setShowAssignPhoneNumbers(null); refetchUseCases(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Do Not Call list section
// ---------------------------------------------------------------------------

type DncMode =
  | { mode: 'list' }
  | { mode: 'adding' };

function DncSection({ outboundSubmitted }: { outboundSubmitted: boolean }) {
  const { data: entries, loading, refetch } = useFetchData<DncEntry[]>('/dashboard/api/compliance/dnc', []);
  const [dncMode, setDncMode] = useState<DncMode>({ mode: 'list' });
  const [search, setSearch] = useState('');

  const filtered = entries.filter(e =>
    !search || e.phone_number.includes(search.replace(/\D/g, ''))
  );

  return (
    <Section>
      <SectionHeaderRow>
        <SectionLabel>Do Not Call List</SectionLabel>
        {outboundSubmitted && (
          <ActionChip onClick={() => setDncMode({ mode: 'adding' })}>
            <Plus size={12} /> Add Number
          </ActionChip>
        )}
      </SectionHeaderRow>
      <Card>
        {!outboundSubmitted ? (
          <FormCard locked>
            <FormInfo>
              <FormLabel style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Lock size={14} /> Do Not Call List
              </FormLabel>
              <FormDescription>Requires Outbound Permissions to be approved</FormDescription>
            </FormInfo>
            <Badge variant="default">Locked</Badge>
          </FormCard>
        ) : loading ? (
          <Table><Tbody><SkeletonRows cols={2} rows={3} /></Tbody></Table>
        ) : entries.length === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState icon={<PhoneOff size={32} />} title="No DNC entries" body="Numbers added here or by your AI agents will not be called." />
          </div>
        ) : (
          <>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--bg-border)' }}>
              <Input
                placeholder="Search by phone number…"
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                style={{ maxWidth: 280, height: 32, fontSize: 13 }}
              />
            </div>
            <PhoneScrollArea>
              <Table>
                <Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' }}>
                  <tr>
                    <th>Phone Number</th>
                    <th>Date Added</th>
                  </tr>
                </Thead>
                <Tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No matching entries</td></tr>
                  ) : filtered.map(entry => (
                    <tr key={entry.phone_number}>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatPhone(entry.phone_number)}</td>
                      <td>{entry.added_datetime ? new Date(entry.added_datetime).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </Tbody>
              </Table>
            </PhoneScrollArea>
            <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--bg-border)' }}>
              {filtered.length === entries.length
                ? `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`
                : `${filtered.length} of ${entries.length} entries`}
            </div>
          </>
        )}
      </Card>

      {dncMode.mode === 'adding' && (
        <AddDncModal
          onClose={() => setDncMode({ mode: 'list' })}
          onAdded={() => { setDncMode({ mode: 'list' }); refetch(); }}
        />
      )}
    </Section>
  );
}

function AddDncModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidPhone(phone)) {
      setError('Please enter a valid phone number (10 or 11 digits).');
      return;
    }
    setSaving(true);
    try {
      await fetchOrThrow('/dashboard/api/compliance/dnc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
        body: JSON.stringify({ phone_number: phone }),
      });
      showToast('Phone number added to DNC list');
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add number.');
      setSaving(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ModalTitle>Add to Do Not Call List</ModalTitle>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <label>Phone Number</label>
              <Input
                placeholder="+1 (555) 123-4567"
                value={phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPhone(e.target.value); setError(''); }}
                autoFocus
              />
              {error && <span style={{ color: 'var(--accent-red)', fontSize: 12 }}>{error}</span>}
            </FormGroup>
          </ModalBody>
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add to DNC List'}</PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}


// ---------------------------------------------------------------------------
// Phone form cell (CNAM / SHAKEN/STIR per-number status + action)
// ---------------------------------------------------------------------------

function PhoneFormCell({ form, onRegister, onView, registerLabel }: {
  form?: ComplianceForm;
  onRegister: () => void;
  onView: () => void;
  registerLabel: string;
}) {
  if (!form || form.status === 'rejected' || form.status === 'revoked') {
    return (
      <RegisterChip onClick={onRegister}>
        {form?.status === 'rejected' || form?.status === 'revoked' ? 'Re-register' : registerLabel}
      </RegisterChip>
    );
  }
  return (
    <RegisterChip onClick={onView}>
      <Badge variant={STATUS_BADGE_VARIANT[form.status] || 'default'} style={{ fontSize: 9, padding: '1px 5px' }}>
        {STATUS_LABEL[form.status] || form.status}
      </Badge>
    </RegisterChip>
  );
}

// ---------------------------------------------------------------------------
// Copy number button
// ---------------------------------------------------------------------------

function CopyNumber({ number }: { number: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <CopyButton onClick={() => {
      navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatPhone(number)}</span>
      {copied ? <Check size={10} /> : <Copy size={10} />}
    </CopyButton>
  );
}

// ---------------------------------------------------------------------------
// Create Use Case Modal
// ---------------------------------------------------------------------------

function CreateUseCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (!name.trim() || !type) return;
    setSaving(true);
    try {
      await fetchOrThrow('/dashboard/api/compliance/use-cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
        body: JSON.stringify({ name: name.trim(), use_case_type: type }),
      });
      showToast('Use case created', 'success');
      onCreated();
    } catch (e) {
      showToast((e as Error).message || 'Failed to create use case', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={e => e.stopPropagation()}>
        <ModalTitle>New Use Case</ModalTitle>
        <ModalBody>
          <FormGroup>
            <label>Name *</label>
            <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder="e.g. Collections, Appointments" />
          </FormGroup>
          <FormGroup>
            <label>Type *</label>
            <Select value={type} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value)}>
              <option value="">Select...</option>
              {USE_CASE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </FormGroup>
        </ModalBody>
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleCreate} disabled={saving || !name.trim() || !type}>
            {saving ? 'Creating...' : 'Create'}
          </PrimaryButton>
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Assign Campaigns Modal
// ---------------------------------------------------------------------------

function AssignPhoneNumbersModal({ useCaseId, useCaseName, existingPhoneNumberIds, phoneNumbers, onClose, onSaved }: {
  useCaseId: string;
  useCaseName: string;
  existingPhoneNumberIds: string[];
  phoneNumbers: CompliancePhoneNumber[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(existingPhoneNumberIds));
  const [saving, setSaving] = useState(false);

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await fetchOrThrow(`/dashboard/api/compliance/use-cases/${useCaseId}/phone-numbers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
        body: JSON.stringify({ phone_number_ids: Array.from(selected) }),
      });
      showToast('Phone numbers updated', 'success');
      onSaved();
    } catch (e) {
      showToast((e as Error).message || 'Failed to update phone numbers', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={e => e.stopPropagation()}>
        <ModalTitle>Assign Phone Numbers</ModalTitle>
        <ModalBody>
          {phoneNumbers.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No phone numbers found. Purchase a phone number first.</div>
          ) : phoneNumbers.map(pn => {
            const otherUc = (pn.assigned_use_case || pn.messaging_use_case) && (pn.assigned_use_case || pn.messaging_use_case) !== useCaseName
              ? (pn.assigned_use_case || pn.messaging_use_case) : null;
            const messagingBlocked = !!(pn.messaging_use_case && pn.messaging_use_case !== useCaseName);
            return (
              <div key={pn.phone_number_id} style={{ padding: '8px 0', borderBottom: '1px solid var(--bg-border)' }}>
                <Checkbox checked={selected.has(pn.phone_number_id)} onChange={() => toggle(pn.phone_number_id)} disabled={messagingBlocked}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatPhone(pn.phone_number)}</span>
                  {pn.label && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{pn.label}</span>}
                  {otherUc && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 11 }}>{messagingBlocked ? `Messaging enabled on ${otherUc}` : `Used by ${otherUc}`}</span>}
                </Checkbox>
              </div>
            );
          })}
        </ModalBody>
        <ModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Form editor view
// ---------------------------------------------------------------------------

interface FormEditorProps {
  formType: string;
  formId?: string;
  prefillFromId?: string;
  phoneNumberId?: string;
  useCaseId?: string;
  useCaseType?: string;
  onBack: () => void;
}

function FormEditorView({ formType, formId, prefillFromId, phoneNumberId, useCaseId, useCaseType, onBack }: FormEditorProps) {
  const [data, setData] = useState<Record<string, any>>(
    formType === 'sms_campaign' && useCaseType && !formId ? { campaign_type: useCaseType } : {},
  );
  const [status, setStatus] = useState<string>('draft');
  const [reviewNotes, setReviewNotes] = useState<string | null>(null);
  const [activeFormId, setActiveFormId] = useState<string | null>(formId || null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [loaded, setLoaded] = useState(!formId && !prefillFromId);

  const { data: existingForm } = useFetchData<ComplianceForm | null>(
    formId ? `/dashboard/api/compliance/forms/${formId}` : '',
    null,
  );

  const { data: prefillForm } = useFetchData<ComplianceForm | null>(
    prefillFromId && !formId ? `/dashboard/api/compliance/forms/${prefillFromId}` : '',
    null,
  );

  const { data: allForms } = useFetchData<ComplianceForm[]>(
    '/dashboard/api/compliance/forms',
    [],
  );
  const smsBrandMissing = formType === 'sms_campaign' && !allForms.some(
    f => f.form_type === 'sms_brand' && !['rejected', 'revoked'].includes(f.status)
  );

  React.useEffect(() => {
    if (existingForm) {
      setData(existingForm.data || {});
      setStatus(existingForm.status);
      setReviewNotes(existingForm.review_notes);
      setActiveFormId(existingForm.id);
      setLoaded(true);
    }
  }, [existingForm]);

  React.useEffect(() => {
    if (prefillForm && !formId) {
      setData(prefillForm.data || {});
      setLoaded(true);
    }
  }, [prefillForm, formId]);

  function updateField(field: string, value: any) {
    setData(prev => ({ ...prev, [field]: value }));
  }

  const editable = status === 'draft' || status === 'changes_requested';

  async function handleSave() {
    setSaving(true);
    try {
      if (!activeFormId) {
        const res = await fetchOrThrow('/dashboard/api/compliance/forms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ form_type: formType, use_case_id: useCaseId, phone_number_id: phoneNumberId, data }),
        });
        const result = await res.json();
        setActiveFormId(result.id);
        setStatus('draft');
        showToast('Draft saved', 'success');
      } else {
        await fetchOrThrow(`/dashboard/api/compliance/forms/${activeFormId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ data }),
        });
        showToast('Draft saved', 'success');
      }
    } catch (e) {
      showToast((e as Error).message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    setShowErrors(true);
    setSubmitting(true);
    try {
      let fid = activeFormId;
      if (!fid) {
        const res = await fetchOrThrow('/dashboard/api/compliance/forms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ form_type: formType, use_case_id: useCaseId, phone_number_id: phoneNumberId, data }),
        });
        const result = await res.json();
        fid = result.id;
        setActiveFormId(fid);
      } else {
        await fetchOrThrow(`/dashboard/api/compliance/forms/${fid}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ data }),
        });
      }

      await fetchOrThrow(`/dashboard/api/compliance/forms/${fid}/submit`, {
        method: 'POST',
        headers: { [CSRF_HEADER_NAME]: getCSRFToken() },
      });
      setStatus('pending_review');
      showToast('Form submitted for review', 'success');
    } catch (e) {
      const msg = (e as Error).message || 'Submission failed';
      try {
        const jsonStr = msg.substring(msg.indexOf('{'));
        const parsed = JSON.parse(jsonStr);
        const detail = parsed.detail;
        showToast(Array.isArray(detail) ? detail.join(', ') : detail || msg, 'error');
      } catch {
        showToast(msg, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!loaded) return null;

  return (
    <div>
      <BackButton onClick={onBack}><ArrowLeft size={14} /> Back to Compliance</BackButton>
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PageTitle>{FORM_TITLES[formType] || formType}</PageTitle>
          <Badge variant={STATUS_BADGE_VARIANT[status] || 'default'}>
            {STATUS_LABEL[status] || status}
          </Badge>
        </div>
        {editable && (
          <div style={{ display: 'flex', gap: 8 }}>
            <SecondaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Draft'}
            </SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </PrimaryButton>
          </div>
        )}
      </PageHeader>

      {reviewNotes && status === 'changes_requested' && (
        <ReviewNotesBox>
          <strong>Reviewer notes:</strong> {reviewNotes}
        </ReviewNotesBox>
      )}

      <Card style={{ padding: 'var(--card-padding)' }}>
        {formType === 'outbound_dialing' && <OutboundDialingFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {formType === 'sms_brand' && <SmsBrandFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {formType === 'analytics_provider' && <AnalyticsProviderFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {formType === 'cnam' && <CnamFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {formType === 'port_number' && <PortNumberFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {formType === 'sms_campaign' && smsBrandMissing && <SmsBrandFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
        {(formType === 'sms_campaign' || formType === 'sms_phone_number') && <SmsFields data={data} onChange={updateField} disabled={!editable} showErrors={showErrors} />}
      </Card>

      <PreviousSubmissions
        forms={allForms}
        formType={formType}
        useCaseId={useCaseId}
        phoneNumberId={phoneNumberId}
        currentFormId={activeFormId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Previous submissions
// ---------------------------------------------------------------------------

function PreviousSubmissions({ forms, formType, useCaseId, phoneNumberId, currentFormId }: {
  forms: ComplianceForm[];
  formType: string;
  useCaseId?: string;
  phoneNumberId?: string;
  currentFormId: string | null;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const previous = forms.filter(f => {
    if (f.form_type !== formType) return false;
    if (f.id === currentFormId) return false;
    if (useCaseId && f.use_case_id !== useCaseId) return false;
    if (phoneNumberId && f.phone_number_id !== phoneNumberId) return false;
    return f.status !== 'draft';
  });

  if (!previous.length) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <SectionLabel>Previous Submissions</SectionLabel>
      <Card>
        {previous.map(f => (
          <div key={f.id}>
            <FormCard onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}>
              <FormInfo>
                <FormLabel style={{ fontSize: 13 }}>
                  {f.submitted_at ? new Date(f.submitted_at).toLocaleDateString() : 'Not submitted'}
                </FormLabel>
                <FormDescription>
                  {f.review_notes || (f.reviewed_at ? `Reviewed ${new Date(f.reviewed_at).toLocaleDateString()}` : '')}
                </FormDescription>
              </FormInfo>
              <Badge variant={STATUS_BADGE_VARIANT[f.status] || 'default'}>
                {STATUS_LABEL[f.status] || f.status}
              </Badge>
            </FormCard>
            {expandedId === f.id && <PreviousSubmissionDetail formId={f.id} />}
          </div>
        ))}
      </Card>
    </div>
  );
}

function PreviousSubmissionDetail({ formId }: { formId: string }) {
  const { data: form, loading } = useFetchData<ComplianceForm | null>(
    `/dashboard/api/compliance/forms/${formId}`,
    null,
  );

  if (loading || !form) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading...</div>;

  const data = form.data || {};
  return (
    <div style={{ padding: '0 16px 16px', borderBottom: '1px solid var(--border-primary)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {Object.entries(data).map(([key, value]) => (
            <tr key={key}>
              <td style={{ padding: '3px 12px 3px 0', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                {key.replace(/_/g, ' ')}
              </td>
              <td style={{ padding: '3px 0', fontSize: 12, color: 'var(--text-primary)' }}>
                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? '')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {form.status_history && form.status_history.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Status History</div>
          {form.status_history.map((h) => (
            <div key={h.at ?? h.status} style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>
              <Badge variant={STATUS_BADGE_VARIANT[h.status] || 'default'} style={{ fontSize: 9, padding: '1px 4px', marginRight: 6 }}>
                {STATUS_LABEL[h.status] || h.status}
              </Badge>
              {h.at ? new Date(h.at).toLocaleString() : ''}
              {h.notes ? ` — ${h.notes}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared form field helpers
// ---------------------------------------------------------------------------

interface FieldProps {
  data: Record<string, any>;
  onChange: (field: string, value: any) => void;
  disabled: boolean;
  showErrors?: boolean;
}

function TextField({ label, field, data, onChange, disabled, required, placeholder, multiline, showErrors, validate }: FieldProps & { label: string; field: string; required?: boolean; placeholder?: string; multiline?: boolean; validate?: (v: string) => boolean }) {
  const Component = multiline ? Textarea : Input;
  const value = data[field] || '';
  const missingRequired = showErrors && required && !value;
  const failsFormat = !!value && !!validate && !validate(value);
  const hasError = missingRequired || failsFormat;
  return (
    <FormGroup>
      <label>{label}{required && ' *'}</label>
      <Component
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(field, e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        style={hasError ? { borderColor: 'var(--accent-red)' } : undefined}
      />
      {failsFormat && <span style={{ fontSize: 11, color: 'var(--accent-red)', marginTop: 2 }}>{formatError(validate!)}</span>}
    </FormGroup>
  );
}

function formatError(validate: (v: string) => boolean): string {
  if (validate === isValidEIN) return 'Must be in format 00-0000000';
  if (validate === isValidEmail) return 'Must be a valid email address';
  if (validate === isValidPhone) return 'Must be a valid phone number (10+ digits)';
  if (validate === isValidURL) return 'Must begin with https://';
  if (validate === isValidZip) return 'Must be a valid ZIP code (e.g. 12345)';
  if (validate === isValidCNAM) return 'Must be 15 chars or fewer, start with a letter, and contain only letters, numbers, periods, commas, and spaces';
  return 'Invalid format';
}

function SelectField({ label, field, data, onChange, disabled, required, options, showErrors }: FieldProps & { label: string; field: string; required?: boolean; options: { value: string; label: string }[] }) {
  const hasError = showErrors && required && !data[field];
  return (
    <FormGroup>
      <label>{label}{required && ' *'}</label>
      <Select
        value={data[field] || ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(field, e.target.value)}
        disabled={disabled}
        style={hasError ? { borderColor: 'var(--accent-red)' } : undefined}
      >
        <option value="">Select...</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    </FormGroup>
  );
}

// ---------------------------------------------------------------------------
// Outbound Dialing fields
// ---------------------------------------------------------------------------

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
].map(v => ({ value: v, label: v }));

const BUSINESS_TYPES = [
  'Sole Proprietorship', 'Partnership', 'Limited Liability Corporation',
  'Co-operative', 'Non-profit Corporation', 'Corporation',
].map(v => ({ value: v, label: v }));

const BUSINESS_INDUSTRIES = [
  'Automotive', 'Agriculture', 'Banking', 'Consumer', 'Construction', 'Education',
  'Engineering', 'Energy', 'Oil and Gas', 'Fast Moving Consumer Goods', 'Financial',
  'Fintech', 'Food and Beverage', 'Government', 'Healthcare', 'Hospitality',
  'Insurance', 'Legal', 'Manufacturing', 'Media', 'Online', 'Professional Services',
  'Raw Materials', 'Real Estate', 'Religion', 'Retail', 'Jewelry', 'Technology',
  'Telecommunications', 'Transportation', 'Travel', 'Electronics', 'Not For Profit',
].map(v => ({ value: v, label: v }));

const PHONE_NUMBER_PLANS = [
  { value: 'I plan on purchasing phone numbers', label: 'I plan on purchasing phone numbers' },
  { value: 'My own phone numbers or telephony system', label: 'My own phone numbers or telephony system' },
  { value: 'Uncertain', label: 'Uncertain' },
];

const YES_NO = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];

function OutboundDialingFields({ data, onChange, disabled, showErrors }: FieldProps) {
  const fp = { data, onChange, disabled, showErrors };
  return (
    <>
      <SectionLabel>Business Identity</SectionLabel>
      <TextField label="Legal Business Name" field="legal_business_name" {...fp} required />
      <TextField label="DBA or Trade Name" field="dba_name" {...fp} placeholder="If different from legal name" />
      <TextField label="Parent Company Legal Name" field="parent_company_name" {...fp} />
      <FormRow>
        <SelectField label="Business Type" field="business_type" {...fp} required options={BUSINESS_TYPES} />
        <SelectField label="Business Industry" field="business_industry" {...fp} required options={BUSINESS_INDUSTRIES} />
      </FormRow>
      <TextField label="USA Employer Identification Number (EIN)" field="ein" {...fp} required placeholder="00-0000000" validate={isValidEIN} />
      <TextField label="Street Address" field="physical_address_street1" {...fp} required />
      <TextField label="Street Address Line 2" field="physical_address_street2" {...fp} />
      <FormRow>
        <TextField label="City" field="physical_address_city" {...fp} required />
        <SelectField label="State (US)" field="physical_address_state" {...fp} options={US_STATES} />
      </FormRow>
      <FormRow>
        <TextField label="Postal Code" field="physical_address_postal_code" {...fp} required validate={isValidZip} />
        <TextField label="Country" field="physical_address_country" {...fp} />
      </FormRow>
      <TextField label="Business Registration Address" field="registration_address" {...fp} placeholder="If same as physical, enter 'Same as physical address'" />
      <TextField label="Business Website" field="business_website" {...fp} required placeholder="https://" validate={isValidURL} />

      <SectionLabel style={{ marginTop: 32 }}>Point of Contact</SectionLabel>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        This is the person with authority to control or manage the company and bind it to agreements.
      </p>
      <FormRow>
        <TextField label="First Name" field="poc_first_name" {...fp} required />
        <TextField label="Last Name" field="poc_last_name" {...fp} required />
      </FormRow>
      <FormRow>
        <TextField label="Email" field="poc_email" {...fp} required validate={isValidEmail} />
        <TextField label="Phone Number" field="poc_phone_number" {...fp} required validate={isValidPhone} />
      </FormRow>
      <TextField label="Job Title" field="poc_job_title" {...fp} required />
      <SelectField label="Are you authorized to enter into agreements on behalf of this company?" field="poc_authorized" {...fp} required options={YES_NO} />

      <SectionLabel style={{ marginTop: 32 }}>Billing Contact</SectionLabel>
      <TextField label="Full Name" field="billing_contact_name" {...fp} required />
      <TextField label="Email" field="billing_contact_email" {...fp} required validate={isValidEmail} />
      <TextField label="Job Title / Position" field="billing_contact_title" {...fp} required />

      <SectionLabel style={{ marginTop: 32 }}>Compliance Contact</SectionLabel>
      <TextField label="Full Name" field="compliance_contact_name" {...fp} required />
      <TextField label="Email" field="compliance_contact_email" {...fp} required validate={isValidEmail} />
      <TextField label="Job Title / Position" field="compliance_contact_title" {...fp} required />

      <SectionLabel style={{ marginTop: 32 }}>Call Setup</SectionLabel>
      <FormGroup>
        <label>What types of phone calls will you be making? *</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {['Outbound', 'Inbound', 'Web/Browser via WebRTC'].map(t => (
            <Checkbox
              key={t}
              checked={(data.call_types || []).includes(t)}
              disabled={disabled}
              hasError={showErrors && !(data.call_types?.length)}
              onChange={v => {
                const current = data.call_types || [];
                onChange('call_types', v ? [...current, t] : current.filter((x: string) => x !== t));
              }}
            >
              {t}
            </Checkbox>
          ))}
        </div>
      </FormGroup>
      <TextField label="What are the use cases you are interested in, and what are the goals for each?" field="use_case_description" {...fp} required multiline />
      <SelectField label="Do you plan on purchasing phone numbers, or do you already have your own?" field="phone_number_plan" {...fp} required options={PHONE_NUMBER_PLANS} />
      {data.phone_number_plan === 'My own phone numbers or telephony system' && (
        <TextField label="Please specify your phone number management, telephony, or VoIP provider" field="phone_number_provider" {...fp} required />
      )}
      <SelectField label="Will Protected Health Information (PHI) be communicated or collected?" field="involves_phi" {...fp} required options={YES_NO} />
      <SelectField label="Will Cardholder Data (CHD) be collected?" field="involves_chd" {...fp} required options={YES_NO} />

      <SectionLabel style={{ marginTop: 32 }}>Contact List</SectionLabel>
      <SelectField label="Did you purchase, rent, or otherwise acquire these contacts from a third party?" field="purchased_contacts" {...fp} required options={YES_NO} />
      {data.purchased_contacts === 'Yes' && (
        <>
          <TextField label="Name of seller, vendor, or data broker" field="vendor_name" {...fp} required />
          <TextField label="Date acquired" field="vendor_date_acquired" {...fp} required />
          <TextField label="What did the vendor inform you about contact consent? Did contacts agree to be called, and can the vendor provide proof?" field="vendor_consent_info" {...fp} required multiline />
          <TextField label="Did contacts opt in to your company specifically, or to the vendor / another third party?" field="vendor_opt_in_specificity" {...fp} required />
          <SelectField label="Has the list been scrubbed against DNC registries (national, state, etc)?" field="vendor_dnc_scrubbed" {...fp} required options={YES_NO} />
          {data.vendor_dnc_scrubbed === 'Yes' && (
            <TextField label="Date of DNC scrub" field="vendor_dnc_scrub_date" {...fp} required />
          )}
          <SelectField label="Intended call type" field="vendor_call_type" {...fp} required options={[
            { value: 'Marketing', label: 'Marketing' },
            { value: 'Transactional', label: 'Transactional' },
            { value: 'Informational', label: 'Informational' },
          ]} />
          <FormGroup>
            <Checkbox
              checked={!!data.vendor_tcpa_acknowledgement}
              disabled={disabled}
              hasError={showErrors && !data.vendor_tcpa_acknowledgement}
              onChange={v => onChange('vendor_tcpa_acknowledgement', v)}
            >
              I acknowledge that purchased or transferred consent is not valid prior express written consent for autodialed, prerecorded, or AI-voice calls under the TCPA, and that my company remains responsible for obtaining valid, caller-specific consent. *
            </Checkbox>
          </FormGroup>
        </>
      )}
      {data.purchased_contacts === 'No' && (
        <>
          <TextField label="Source/method of contact list (e.g. web form, point of sale, inbound inquiry, existing customer relationship)" field="contact_source_method" {...fp} required />
          <TextField label="Date and method consent was captured" field="consent_capture_method" {...fp} required />
          <SelectField label="Can you produce consent records on request?" field="consent_records_available" {...fp} required options={YES_NO} />
        </>
      )}

      <SectionLabel style={{ marginTop: 32 }}>Telecom Compliance</SectionLabel>
      <SelectField label="Are you registered with the FCC's Robocall Mitigation Database (RMD)?" field="rmd_registered" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Not applicable', label: 'Not applicable — we do not originate PSTN traffic' },
      ]} />
      {data.rmd_registered === 'Yes' && (
        <TextField label="RMD Filing ID" field="rmd_filing_id" {...fp} required />
      )}
      <SelectField label="Do you originate, route, or resell outbound calls to the PSTN on behalf of others?" field="originates_for_others" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Not applicable', label: 'Not applicable' },
      ]} />
      {data.originates_for_others === 'Yes' && (
        <>
          <SelectField label="Do you confirm that all voice service providers you support are registered in the FCC RMD and in good standing?" field="others_rmd_confirmed" {...fp} required options={YES_NO} />
          {data.others_rmd_confirmed === 'Yes' && (
            <TextField label="Please list the RMD Filing IDs for all voice service providers you support" field="others_rmd_filing_ids" {...fp} required multiline />
          )}
          <TextField label="Describe your KYC and onboarding process for your end customers" field="others_kyc_process" {...fp} required multiline />
        </>
      )}
      <SelectField label="Do you certify that all calls and messages sent through this platform will comply with the TCPA, TSR, and applicable state telemarketing laws?" field="tcpa_tsr_certification" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'N/A', label: 'N/A — informational/non-telemarketing traffic only' },
      ]} />
      <TextField label="How do recipients provide consent to receive calls or messages from you?" field="consent_method" {...fp} required multiline placeholder="Describe the method of consent, e.g. website checkbox, verbal opt-in, signed contract. Please include relevant links." />
      <SelectField label="Do you maintain records of consent and honor opt-out/revocation requests?" field="maintains_consent_records" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'We are working on implementing this', label: 'We are working on implementing this' },
      ]} />
      <SelectField label="Do you validate outbound calls against the National Do Not Call Registry?" field="validates_dncr" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'N/A', label: 'N/A — informational/non-telemarketing traffic only' },
      ]} />
      {data.validates_dncr === 'Yes' && (
        <SelectField label="How often do you validate against the DNC Registry?" field="dncr_frequency" {...fp} required options={[
          { value: 'Before every campaign', label: 'Before every campaign' },
          { value: 'Daily', label: 'Daily' },
          { value: 'Weekly', label: 'Weekly' },
          { value: 'Monthly', label: 'Monthly' },
          { value: 'Quarterly', label: 'Quarterly' },
          { value: 'Other', label: 'Other' },
        ]} />
      )}
      <SelectField label="Do you honor internal Do Not Call (DNC) lists?" field="honors_internal_dnc" {...fp} required options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'N/A', label: 'N/A' },
      ]} />
      <FormGroup>
        <Checkbox
          checked={!!data.rmd_attestation}
          disabled={disabled}
          hasError={showErrors && !data.rmd_attestation}
          onChange={v => onChange('rmd_attestation', v)}
        >
          I confirm our organization is registered in the FCC Robocall Mitigation Database or is not subject to this requirement. *
        </Checkbox>
      </FormGroup>
    </>
  );
}

// ---------------------------------------------------------------------------
// SMS Brand Registration fields
// ---------------------------------------------------------------------------

function SmsBrandFields({ data, onChange, disabled, showErrors }: FieldProps) {
  const fp = { data, onChange, disabled, showErrors };
  return (
    <>
      <SectionLabel>Brand Details</SectionLabel>
      <TextField label="Legal Company Name" field="legal_company_name" {...fp} required />
      <TextField label="DBA / Brand Name" field="dba_brand_name" {...fp} placeholder="If different from legal name" />
      <FormRow>
        <SelectField label="Company Type" field="company_type" {...fp} required options={COMPANY_TYPES} />
        <TextField label="Tax Number / EIN" field="tax_number_ein" {...fp} required validate={isValidEIN} />
      </FormRow>
      <FormRow>
        <TextField label="DUNS Number" field="alt_id_duns" {...fp} />
        <TextField label="GIIN Number" field="alt_id_giins" {...fp} />
      </FormRow>
      <TextField label="LEI Number" field="alt_id_lei" {...fp} />

      <SectionLabel style={{ marginTop: 32 }}>Address</SectionLabel>
      <TextField label="Street Address" field="address_street" {...fp} required />
      <FormRow>
        <TextField label="City" field="address_city" {...fp} required />
        <SelectField label="State" field="address_state" {...fp} required options={US_STATES} />
      </FormRow>
      <TextField label="ZIP Code" field="address_zip" {...fp} required validate={isValidZip} />

      <SectionLabel style={{ marginTop: 32 }}>Other</SectionLabel>
      <TextField label="Website" field="website" {...fp} required placeholder="https://" validate={isValidURL} />
      {data.company_type === 'Public' && (
        <FormRow>
          <TextField label="Stock Symbol" field="stock_symbol" {...fp} required />
          <TextField label="Stock Exchange" field="stock_exchange" {...fp} required />
        </FormRow>
      )}
      <TextField label="Vertical" field="vertical" {...fp} placeholder="Industry vertical" />

      <SectionLabel style={{ marginTop: 32 }}>Business Contact</SectionLabel>
      <FormRow>
        <TextField label="First Name" field="biz_first_name" {...fp} required />
        <TextField label="Last Name" field="biz_last_name" {...fp} required />
      </FormRow>
      <FormRow>
        <TextField label="Email" field="biz_email" {...fp} required validate={isValidEmail} />
        <TextField label="Mobile Phone" field="biz_mobile_phone" {...fp} required validate={isValidPhone} />
      </FormRow>

      <SectionLabel style={{ marginTop: 32 }}>Support Contact</SectionLabel>
      <FormRow>
        <TextField label="Support Email" field="support_email" {...fp} required validate={isValidEmail} />
        <TextField label="Support Phone" field="support_phone" {...fp} required validate={isValidPhone} />
      </FormRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// Analytics Provider fields (simplified to single attestation)
// ---------------------------------------------------------------------------

function AnalyticsProviderFields({ data, onChange, disabled, showErrors }: FieldProps) {
  return (
    <>
      <SectionLabel>Analytics Provider Attestation</SectionLabel>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Confirm that your phone numbers are registered with the Free Caller Registry before going live.
      </p>
      <FormGroup>
        <Checkbox
          checked={!!data.freecallerregistry_attestation}
          disabled={disabled}
          hasError={showErrors && !data.freecallerregistry_attestation}
          onChange={v => onChange('freecallerregistry_attestation', v)}
        >
          I confirm I have registered or will register our phone numbers at <a href="https://www.freecallerregistry.com" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent-cyan)' }}>freecallerregistry.com</a> prior to going live. *
        </Checkbox>
      </FormGroup>
    </>
  );
}

// ---------------------------------------------------------------------------
// CNAM fields
// ---------------------------------------------------------------------------

function CnamFields({ data, onChange, disabled, showErrors }: FieldProps) {
  const fp = { data, onChange, disabled, showErrors };
  return (
    <>
      <TextField label="Desired CallerID Display Name" field="caller_id_display_name" {...fp} required placeholder="15 character max, must begin with a letter" validate={isValidCNAM} />
      <SelectField label="CallerID name ownership" field="name_ownership" {...fp} required options={[
        { value: 'Yes, this name belongs to my organization', label: 'Yes, this name belongs to my organization' },
        { value: 'No, I am authorized to use this name on behalf of another organization', label: 'No, I am authorized on behalf of another organization' },
        { value: 'No, and I am not authorized', label: 'No, and I am not authorized' },
      ]} />
      {data.name_ownership === 'No, I am authorized to use this name on behalf of another organization' && (
        <FormGroup>
          <label>Authorization Letter (signed, on letterhead) *</label>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            Must include the authorizing party's name, title, and signature.
          </p>
          <Input type="file" disabled={disabled} onChange={() => { /* TODO: file upload */ }} />
        </FormGroup>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Port a Number fields (CarrierX LOA)
// ---------------------------------------------------------------------------

function PortNumberFields({ data, onChange, disabled, showErrors }: FieldProps) {
  const fp = { data, onChange, disabled, showErrors };
  return (
    <>
      <SectionLabel>Customer Information</SectionLabel>
      <FormRow>
        <TextField label="Full Name" field="full_name" {...fp} required />
        <TextField label="Company" field="company" {...fp} required />
      </FormRow>

      <SectionLabel style={{ marginTop: 32 }}>Existing Account</SectionLabel>
      <TextField label="Current Service Provider" field="service_provider_name" {...fp} required />
      <FormRow>
        <TextField label="Account Number" field="account_number" {...fp} required />
        <TextField label="Account PIN" field="pin" {...fp} required />
      </FormRow>
      <TextField label="Service Address" field="address" {...fp} required />
      <TextField label="Address Line 2" field="address2" {...fp} placeholder="Suite, unit, etc." />
      <TextField label="City, State, ZIP" field="city_state_zip" {...fp} required />

      <SectionLabel style={{ marginTop: 32 }}>Numbers & Services</SectionLabel>
      <TextField label="Phone Numbers to Port" field="phone_numbers_to_port" {...fp} required multiline placeholder="One number per line" />
      <FormGroup>
        <label>Services *</label>
        <div style={{ display: 'flex', gap: 16 }}>
          <Checkbox
            checked={!!data.services_voice}
            disabled={disabled}
            hasError={showErrors && !data.services_voice && !data.services_messaging}
            onChange={v => onChange('services_voice', v)}
          >
            Voice
          </Checkbox>
          <Checkbox
            checked={!!data.services_messaging}
            disabled={disabled}
            hasError={showErrors && !data.services_voice && !data.services_messaging}
            onChange={v => onChange('services_messaging', v)}
          >
            Messaging (SMS/MMS)
          </Checkbox>
        </div>
      </FormGroup>
      <FormGroup>
        <label>Requested Effective Date *</label>
        <Input
          type="date"
          value={data.requested_effective_date || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('requested_effective_date', e.target.value)}
          disabled={disabled}
          style={showErrors && !data.requested_effective_date ? { borderColor: 'var(--accent-red)' } : undefined}
        />
      </FormGroup>

      <SectionLabel style={{ marginTop: 32 }}>Signature</SectionLabel>
      <TextField label="Signature (type your full name)" field="signature_name" {...fp} required />
      <FormGroup>
        <label>Date</label>
        <Input type="text" value={new Date().toLocaleDateString()} disabled />
      </FormGroup>
    </>
  );
}

// ---------------------------------------------------------------------------
// SMS Campaign fields
// ---------------------------------------------------------------------------

const SMS_TRAFFIC_TYPES = [
  { value: 'Transactional (Receipts, alerts)', label: 'Transactional (Receipts, alerts)' },
  { value: 'Customer care', label: 'Customer care' },
  { value: 'Marketing / promotions', label: 'Marketing / promotions' },
  { value: 'Mixed', label: 'Mixed' },
];

const MESSAGE_FREQUENCIES = [
  { value: 'One-time', label: 'One-time' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Event-based (describe)', label: 'Event-based (describe)' },
  { value: 'Other (describe)', label: 'Other (describe)' },
];

function SmsFields({ data, onChange, disabled, showErrors }: FieldProps) {
  const fp = { data, onChange, disabled, showErrors };
  return (
    <>
      <SectionLabel>Campaign Details</SectionLabel>
      <SelectField label="Campaign Type" field="campaign_type" {...fp} required options={USE_CASE_TYPES} />
      <TextField label="Campaign Description" field="campaign_description" {...fp} required multiline />
      <TextField label="Call-to-Action / Message Flow" field="cta_message_flow" {...fp} required multiline placeholder="Describe how a consumer opts in, giving consent. Include screenshots of website/application opt-ins." />
      <TextField label="Call-to-Action URL" field="cta_url" {...fp} required validate={isValidURL} />
      <FormRow>
        <TextField label="Terms of Service URL" field="terms_of_service_url" {...fp} required validate={isValidURL} />
        <TextField label="Privacy Policy URL" field="privacy_policy_url" {...fp} required validate={isValidURL} />
      </FormRow>
      <TextField label="Sample Messages" field="sample_messages" {...fp} required multiline placeholder="Include 1 for each type." />

      <SectionLabel style={{ marginTop: 32 }}>Compliance Keyword Responses</SectionLabel>
      <TextField label="Opt-In Keywords" field="opt_in_keywords" {...fp} required placeholder="e.g. UNSTOP, OPTIN" />
      <TextField label="Opt-In Response" field="opt_in_response" {...fp} required multiline placeholder="Must include: brand name, campaign description, support contact, message frequency, 'Msg&data rates may apply'" />
      <TextField label="Opt-Out Response (STOP)" field="opt_out_response" {...fp} required multiline placeholder="Must include brand name, acknowledgement of opt-out, confirmation no further messages" />
      <TextField label="Help Response (HELP)" field="help_response" {...fp} required multiline placeholder="Must include: brand name, campaign description, support contact, message frequency, 'Msg&data rates may apply'" />

      <SectionLabel style={{ marginTop: 32 }}>Usage Disclosures</SectionLabel>
      <FormRow>
        <SelectField label="Number Pooling (50+ numbers)?" field="number_pooling" {...fp} required options={YES_NO} />
        <SelectField label="Direct Lending or Loan Arrangement?" field="direct_lending" {...fp} required options={YES_NO} />
      </FormRow>
      <FormRow>
        <SelectField label="Messages contain embedded links?" field="embedded_link" {...fp} required options={YES_NO} />
        <SelectField label="Messages contain embedded phone numbers?" field="embedded_phone_number" {...fp} required options={YES_NO} />
      </FormRow>
      <SelectField label="Age-gated content?" field="age_gated_content" {...fp} required options={YES_NO} />
      <FormGroup>
        <Checkbox
          checked={!!data.affiliate_marketing_confirmed}
          disabled={disabled}
          hasError={showErrors && !data.affiliate_marketing_confirmed}
          onChange={v => onChange('affiliate_marketing_confirmed', v)}
        >
          I confirm that this campaign will not be used for Affiliate Marketing *
        </Checkbox>
      </FormGroup>

      <SectionLabel style={{ marginTop: 32 }}>Traffic Profile</SectionLabel>
      <SelectField label="What best describes your SMS traffic?" field="sms_traffic_type" {...fp} required options={SMS_TRAFFIC_TYPES} />
      <TextField label="What website or domain is associated with this SMS campaign?" field="associated_website" {...fp} required validate={isValidURL} />
      <SelectField label="Is the website or domain displayed in the messages?" field="website_in_messages" {...fp} required options={YES_NO} />
      <SelectField label="How often will recipients receive messages?" field="message_frequency" {...fp} required options={MESSAGE_FREQUENCIES} />
      {(data.message_frequency === 'Event-based (describe)' || data.message_frequency === 'Other (describe)') && (
        <TextField label="Please describe" field="message_frequency_description" {...fp} required multiline />
      )}
    </>
  );
}
